import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, Plus, Upload, Download, Edit, Trash2, RotateCcw } from 'lucide-react';
import { CsvDownloadButton } from '@/components/CsvDownloadButton';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function MasterDataSection({
  title, icon: Icon, color, items, masterType,
  onAdd, onEdit, onDelete, onUpload, onResetComplete, renderItem
}) {
  const [search, setSearch] = useState('');
  const [resetting, setResetting] = useState(false);

  const bgColor = { blue: 'bg-blue-50', indigo: 'bg-indigo-50', green: 'bg-green-50', purple: 'bg-purple-50', orange: 'bg-orange-50' }[color] || 'bg-gray-50';
  const textColor = { blue: 'text-blue-800', indigo: 'text-indigo-800', green: 'text-green-800', purple: 'text-purple-800', orange: 'text-orange-800' }[color] || 'text-gray-800';
  const iconColor = { blue: 'text-blue-600', indigo: 'text-indigo-600', green: 'text-green-600', purple: 'text-purple-600', orange: 'text-orange-600' }[color] || 'text-gray-600';
  const badgeClass = { blue: 'bg-blue-100 text-blue-800', indigo: 'bg-indigo-100 text-indigo-800', green: 'bg-green-100 text-green-800', purple: 'bg-purple-100 text-purple-800', orange: 'bg-orange-100 text-orange-800' }[color] || 'bg-gray-100';

  const filtered = search
    ? items.filter(item => item.name.toLowerCase().includes(search.toLowerCase()))
    : items;

  async function handleExport() {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(API + `/masters/export/${masterType}`, {
        headers: { Authorization: 'Bearer ' + token },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${masterType}_export.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Downloaded');
    } catch (err) {
      toast.error('Export failed');
    }
  }

  async function handleResetAll() {
    if (!items.length) {
      toast.info(`${title} is already empty`);
      return;
    }
    const cascadeNote =
      masterType === 'brand'
        ? '\n\nNOTE: This will ALSO wipe ALL Sub Brands (FK cascade).'
        : masterType === 'menu'
          ? '\n\nNOTE: This will ALSO wipe ALL Sub Menus (FK cascade).'
          : '';
    const ok = window.confirm(
      `Are you sure you want to RESET (delete ALL) ${items.length} ${title}?\n\n` +
      `This action CANNOT be undone. Existing Products / POs / Invoices that reference these by name will keep working — but the dropdown list will be empty until you re-upload or re-add.` +
      cascadeNote
    );
    if (!ok) return;
    setResetting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.delete(API + `/masters/${masterType}/reset-all`, {
        headers: { Authorization: 'Bearer ' + token },
      });
      const cascaded = (res.data.cascaded || []).map((c) => `${c.deleted} ${c.label}`).join(', ');
      toast.success(
        `Reset ${res.data.deleted} ${res.data.label}` + (cascaded ? ` (also wiped ${cascaded})` : '')
      );
      if (onResetComplete) onResetComplete();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reset failed');
    } finally {
      setResetting(false);
    }
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <div className={`${bgColor} px-4 py-3 flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${iconColor}`} />
          <span className={`font-semibold ${textColor}`}>{title}</span>
          <Badge className={badgeClass}>{items.length}</Badge>
        </div>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" onClick={handleExport} className="gap-1 h-7 text-xs" data-testid={`export-${masterType}-btn`}>
            <Download className="w-3 h-3" />
          </Button>
          <CsvDownloadButton onDownload={handleExport} testId={`export-csv-${masterType}-btn`} className="h-7 text-xs" />
          <Button size="sm" variant="outline" onClick={onUpload} className="gap-1 h-7 text-xs" data-testid={`upload-${masterType}-btn`}>
            <Upload className="w-3 h-3" /> Upload
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleResetAll}
            disabled={resetting}
            className="gap-1 h-7 text-xs border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
            title={`Reset all ${title}`}
            data-testid={`reset-${masterType}-btn`}
          >
            <RotateCcw className="w-3 h-3" /> Reset
          </Button>
          <Button size="sm" onClick={onAdd} className="gap-1 h-7" data-testid={`add-${masterType}-btn`}>
            <Plus className="w-3 h-3" /> Add
          </Button>
        </div>
      </div>
      {/* Search */}
      <div className="px-3 py-2 border-b">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder={`Search ${title.toLowerCase()}...`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 text-xs"
            data-testid={`search-${masterType}`}
          />
        </div>
      </div>
      {/* List */}
      <div className="max-h-56 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="p-4 text-center text-gray-500 text-sm">
            {search ? `No results for "${search}"` : `No ${title.toLowerCase()} yet`}
          </div>
        ) : (
          <ul className="divide-y">
            {filtered.map(item => (
              <li key={item.id} className="px-4 py-2 flex items-center justify-between hover:bg-gray-50">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${item.is_active ? 'bg-green-500' : 'bg-gray-400'}`} />
                  {renderItem ? renderItem(item) : <span className="text-sm truncate">{item.name}</span>}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => onEdit(item)}>
                    <Edit className="w-3 h-3" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500 hover:text-red-700" onClick={() => onDelete(item.id, item.name)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
